import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RotateCw, ThumbsUp, AlertTriangle, MessageSquare, Lightbulb, Target, CheckCircle2, Zap, Download, UserRound } from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useToast } from '../../stores/toastStore'
import { esportaFeedbackPDF } from '../../utils/esportaPDF'
import { api, messaggioErrore } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { ScoreGauge } from '../../components/charts/ScoreGauge'
import { EmptyState } from '../../components/ui/EmptyState'
import { formatDataOra, formatDurata } from '../../lib/format'
import pagina from '../pagine.module.css'

type FeedbackStato = 'nessuno' | 'in_elaborazione' | 'completato' | 'errore'

interface Turno {
  ruolo: 'sdr' | 'lead'
  testo: string
  timestamp: string
}

interface Feedback {
  punti_forti: string[]
  aree_miglioramento: string[]
  citazioni: Array<{ chi: string; testo: string; commento: string }>
  consiglio_pratico: string
  scores: {
    apertura: number
    obiezioni: number
    chiusura: number
    complessivo: number
  }
  obiettivo_raggiunto?: boolean
  obiettivo_score?: number
  obiettivo_commento?: string
}

interface LeadInfo {
  id: string
  nome: string
  ruolo: string | null
  settore: string | null
}

interface SessioneDettaglio {
  id: string
  durataSecondi: number
  trascritto: Turno[]
  feedback: Feedback | null
  scoreApertura: number | null
  scoreObiezioni: number | null
  scoreChiusura: number | null
  votoComplessivo: number | null
  obiettivoScelto: string | null
  obiettivoRaggiunto: boolean | null
  obiettivoScore: number | null
  metodoAvvio: 'preselezionato' | 'ivr' | 'browser' | null
  completata: boolean
  feedbackStato: FeedbackStato
  feedbackLetto: boolean
  feedbackErrore: string | null
  /** Lead usato nella sessione (null se cancellato o mai associato) */
  lead: LeadInfo | null
  createdAt: string
}

export function SessioneDettaglio() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const q = useQuery({
    queryKey: ['sessione', id],
    queryFn: async () => (await api.get<{ dati: SessioneDettaglio }>(`/sessions/${id}`)).data.dati,
    enabled: !!id,
    // Polling 5s SOLO mentre il feedback è in elaborazione (job background
    // dopo la chiusura della sessione). Smette appena lo stato passa a
    // 'completato' o 'errore'.
    refetchInterval: (query) => {
      const d = query.state.data
      return d?.feedbackStato === 'in_elaborazione' ? 5_000 : false
    },
  })

  // Quando l'utente apre il dettaglio e il feedback è pronto ma non letto,
  // segnalo letto al backend → spegne il badge rosso sulla sidebar.
  const sessione = q.data
  useEffect(() => {
    if (!id || !sessione) return
    if (sessione.feedbackStato === 'completato' && !sessione.feedbackLetto) {
      api.post(`/sessions/${id}/segna-letto`).then(() => {
        queryClient.invalidateQueries({ queryKey: ['notifiche-feedback'] })
      }).catch(() => {/* il badge si aggiornerà al prossimo polling */})
    }
  }, [id, sessione, queryClient])

  const rigenera = useMutation({
    mutationFn: async () => api.post(`/sessions/${id}/feedback-rigenerato`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessione', id] }),
    onError: (err) => alert(messaggioErrore(err)),
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Sessione non trovata" />

  const s = q.data
  const f = s.feedback

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <Button
          variante="ghost"
          taglia="sm"
          iconaSinistra={<ArrowLeft size={14} />}
          onClick={() => navigate(-1)}
        >
          Indietro
        </Button>
        <div style={{ display: 'flex', gap: 8 }}>
          <ScaricaPDF sessione={s} />
          <Button
            variante="secondary"
            taglia="sm"
            iconaSinistra={<RotateCw size={14} />}
            onClick={() => rigenera.mutate()}
            caricamento={rigenera.isPending}
          >
            Rigenera feedback
          </Button>
        </div>
      </div>

      {/* Riepilogo */}
      <Card>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
              {formatDataOra(s.createdAt)}
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
              Sessione {s.completata ? 'completata' : 'incompleta'}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge variante="default">Durata {formatDurata(s.durataSecondi)}</Badge>
              <Badge variante={badgeModalitaVariante(s.metodoAvvio)}>
                {etichettaModalita(s.metodoAvvio)}
              </Badge>
            </div>
          </div>

          {s.votoComplessivo != null && (
            <ScoreGauge voto={s.votoComplessivo} etichetta="Voto complessivo" />
          )}
        </div>

        {(s.scoreApertura != null || s.scoreObiezioni != null || s.scoreChiusura != null) && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 20,
            paddingTop: 20, borderTop: '1px solid var(--border)',
          }}>
            <ScoreRiga etichetta="Apertura" valore={s.scoreApertura} max={5} />
            <ScoreRiga etichetta="Obiezioni" valore={s.scoreObiezioni} max={5} />
            <ScoreRiga etichetta="Chiusura" valore={s.scoreChiusura} max={5} />
          </div>
        )}
      </Card>

      {/* Card con riferimento al lead usato nella sessione */}
      <LeadCard lead={s.lead} />

      {/* Card valutazione obiettivo (solo se l'SDR ne aveva scelto uno) */}
      {s.obiettivoScelto && (
        <ObiettivoCard
          obiettivo={s.obiettivoScelto}
          raggiunto={s.obiettivoRaggiunto}
          score={s.obiettivoScore}
          commento={f?.obiettivo_commento}
        />
      )}

      {/* Feedback in elaborazione (job background dopo la chiusura) */}
      {s.feedbackStato === 'in_elaborazione' && !f && (
        <Card>
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <div
              style={{
                width: 48, height: 48,
                border: '3px solid var(--border)',
                borderTopColor: 'var(--accent)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px',
              }}
            />
            <h3 style={{ marginBottom: 6 }}>Feedback in elaborazione…</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
              La pagina si aggiorna automaticamente quando è pronto (di solito entro 30–60 secondi).
            </p>
          </div>
        </Card>
      )}

      {/* Feedback fallito → CTA Riprova */}
      {s.feedbackStato === 'errore' && !f && (
        <Card>
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <AlertTriangle size={36} color="var(--warning)" style={{ marginBottom: 12 }} />
            <h3 style={{ marginBottom: 6 }}>Feedback non disponibile</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
              {s.feedbackErrore ?? 'Si è verificato un errore durante la generazione.'}
            </p>
            <Button
              iconaSinistra={<RotateCw size={14} />}
              onClick={() => rigenera.mutate()}
              caricamento={rigenera.isPending}
            >
              Riprova generazione feedback
            </Button>
          </div>
        </Card>
      )}

      {/* Feedback AI */}
      {f && (
        <div className={pagina.due}>
          <Card titolo="Feedback AI">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {f.punti_forti.length > 0 && (
                <FeedbackBlocco
                  icona={<ThumbsUp size={16} />}
                  titolo="Punti forti"
                  colore="var(--success)"
                  voci={f.punti_forti}
                />
              )}
              {f.aree_miglioramento.length > 0 && (
                <FeedbackBlocco
                  icona={<AlertTriangle size={16} />}
                  titolo="Da migliorare"
                  colore="var(--warning)"
                  voci={f.aree_miglioramento}
                />
              )}
              {f.consiglio_pratico && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: 'var(--accent)' }}>
                    <Lightbulb size={16} />
                    <strong style={{ fontSize: 13 }}>Consiglio pratico</strong>
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.6 }}>{f.consiglio_pratico}</div>
                </div>
              )}
            </div>
          </Card>

          <Card titolo="Citazioni evidenziate">
            {f.citazioni.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {f.citazioni.map((c, i) => (
                  <div key={i} style={{
                    padding: 12,
                    background: 'var(--bg-subtle)',
                    borderLeft: '3px solid var(--accent)',
                    borderRadius: 'var(--radius-sm)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                      {c.chi}
                    </div>
                    <div style={{ fontSize: 13, fontStyle: 'italic', marginBottom: 6 }}>
                      "{c.testo}"
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {c.commento}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState titolo="Nessuna citazione" />
            )}
          </Card>
        </div>
      )}

      {/* Trascritto */}
      <Card titolo={`Trascritto (${s.trascritto.length} turni)`}>
        {s.trascritto.length === 0 ? (
          <EmptyState
            icona={<MessageSquare size={40} />}
            titolo="Nessun trascritto"
            descrizione="La sessione è stata troppo breve o è stata interrotta presto."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {s.trascritto.map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: t.ruolo === 'sdr' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '75%',
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-md)',
                    background: t.ruolo === 'sdr' ? 'var(--accent-soft)' : 'var(--bg-subtle)',
                    color: t.ruolo === 'sdr' ? 'var(--accent)' : 'var(--text-primary)',
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, opacity: 0.7 }}>
                    {t.ruolo === 'sdr' ? 'TU' : 'LEAD'}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5 }}>{t.testo}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function etichettaModalita(m: 'preselezionato' | 'ivr' | 'browser' | null): string {
  if (m === 'browser') return '🎙 Browser'
  if (m === 'preselezionato') return '📞 Telefono (app)'
  if (m === 'ivr') return '📞 Telefono (IVR)'
  return 'Sconosciuta'
}

function badgeModalitaVariante(m: 'preselezionato' | 'ivr' | 'browser' | null): 'accent' | 'info' | 'default' {
  if (m === 'browser') return 'accent'
  if (m === 'preselezionato') return 'accent'
  if (m === 'ivr') return 'info'
  return 'default'
}

/**
 * Card con il riferimento al lead usato nella sessione.
 * Se `lead` è null (record cancellato o sessione senza lead), mostra un
 * placeholder informativo invece di rompersi. Nota: obiettivo NON viene
 * ripetuto qui — è già coperto dall'ObiettivoCard sotto e (per la lista)
 * dal badge nella tabella.
 */
function LeadCard({ lead }: { lead: LeadInfo | null }) {
  const sfondo = lead ? 'var(--accent-soft)' : 'var(--bg-subtle)'
  const colore = lead ? 'var(--accent)' : 'var(--text-secondary)'
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: sfondo, color: colore,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <UserRound size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
          }}>
            Lead usato nella sessione
          </div>
          {lead ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{lead.nome}</div>
              {(lead.ruolo || lead.settore) && (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {[lead.ruolo, lead.settore].filter(Boolean).join(' · ')}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 14, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Lead non disponibile (potrebbe essere stato eliminato).
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

function ObiettivoCard({ obiettivo, raggiunto, score, commento }: {
  obiettivo: string
  raggiunto: boolean | null
  score: number | null
  commento?: string
}) {
  const positivo = raggiunto === true
  // raggiunto può essere null se il feedback non è stato generato; mostriamo
  // il card neutro in attesa di valutazione.
  const sfondo = raggiunto === null ? 'var(--bg-subtle)'
    : positivo ? 'var(--success-soft)' : 'var(--warning-soft)'
  const colore = raggiunto === null ? 'var(--text-secondary)'
    : positivo ? 'var(--success)' : 'var(--warning)'

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: sfondo, color: colore,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {raggiunto === null ? <Target size={22} />
            : positivo ? <CheckCircle2 size={22} /> : <Zap size={22} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
          }}>
            🎯 Obiettivo della chiamata
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>{obiettivo}</div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: commento ? 12 : 0 }}>
            {raggiunto === true && <Badge variante="success">✅ Raggiunto</Badge>}
            {raggiunto === false && <Badge variante="warning">⚡ Non raggiunto</Badge>}
            {raggiunto === null && <Badge variante="default">In attesa di valutazione</Badge>}
            {score != null && (
              <Badge variante="accent">
                <span className="mono">Score {score}/5</span>
              </Badge>
            )}
          </div>

          {commento && (
            <div style={{
              padding: 12, background: 'var(--bg-subtle)',
              borderLeft: `3px solid ${colore}`,
              borderRadius: 'var(--radius-sm)',
              fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)',
            }}>
              {commento}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

function ScoreRiga({ etichetta, valore, max }: { etichetta: string; valore: number | null; max: number }) {
  if (valore == null) return null
  const pct = (valore / max) * 100
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{etichetta}</span>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>{valore}/{max}</span>
      </div>
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: 'var(--accent)',
          borderRadius: 3,
        }} />
      </div>
    </div>
  )
}

function FeedbackBlocco({ icona, titolo, colore, voci }:
  { icona: React.ReactNode; titolo: string; colore: string; voci: string[] }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: colore }}>
        {icona}
        <strong style={{ fontSize: 13 }}>{titolo}</strong>
      </div>
      <ul style={{ listStyle: 'disc', paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
        {voci.map((v, i) => <li key={i}>{v}</li>)}
      </ul>
    </div>
  )
}

/**
 * Pulsante "Scarica PDF" — visibile solo se il feedback è disponibile.
 * Il nome utente viene preso dall'authStore (l'SDR sta guardando una
 * propria sessione). Per manager/superadmin che guardano sessioni di
 * altri membri vediamo "Membro del team" come fallback (il dato
 * `users.nome/cognome` non è in `SessioneDettaglio` lato SDR; aggiungerlo
 * richiederebbe modifica al backend per scopo solo PDF).
 */
function ScaricaPDF({ sessione: s }: { sessione: SessioneDettaglio }) {
  const utente = useAuthStore((st) => st.utente)
  const toast = useToast()
  const [generando, setGenerando] = useState(false)

  if (!s.feedback) return null

  const onClick = async (): Promise<void> => {
    if (!s.feedback) return
    setGenerando(true)
    try {
      await esportaFeedbackPDF({
        id: s.id,
        createdAt: s.createdAt,
        durataSecondi: s.durataSecondi,
        obiettivoScelto: s.obiettivoScelto,
        utente: {
          nome: utente?.nome ?? 'Membro',
          cognome: utente?.cognome ?? 'del team',
        },
        feedback: s.feedback,
      })
    } catch (err) {
      console.error('Errore export PDF', err)
      toast.errore('Errore durante la generazione del PDF.')
    } finally {
      setGenerando(false)
    }
  }

  return (
    <Button
      variante="secondary"
      taglia="sm"
      iconaSinistra={<Download size={14} />}
      onClick={onClick}
      caricamento={generando}
    >
      Scarica PDF
    </Button>
  )
}
